import { atom, RecoilState } from 'recoil';

/**
 * One persisted snapshot of a graph, as emitted by the backend.
 *
 * Matches the JSONB shape written by ``cl.Graph._persist_snapshot`` —
 * every ``.create()`` / ``.update()`` call records an entry with a
 * timestamp, the event kind, and the full graph payload.
 */
export interface GraphSnapshot {
  event: 'create' | 'update';
  recorded_at: string;
  identifier?: string;
  id?: string;
  name?: string;
  description?: string;
  nodes: unknown[];
  edges: unknown[];
  [key: string]: unknown;
}

/**
 * Per-graph timeline keyed by identifier (e.g. ``"Operation Overview"``).
 * Ordered oldest → newest. Populated on chat resume via the
 * ``graph_history`` socket event and appended to on every live
 * ``graph_create`` / ``graph_update`` event.
 */
export const graphHistoryState: RecoilState<Record<string, GraphSnapshot[]>> = atom<
  Record<string, GraphSnapshot[]>
>({
  key: 'GraphHistory',
  default: {}
});

/**
 * Content fingerprint of a snapshot's rendered state — node id + status +
 * active-path flag (so a genuine status transition stays a distinct timeline
 * point) plus the edge set. Used to dedup timeline appends so the resume
 * ``graph_history`` (a full replace) and the resume ``graph_create`` (an append
 * of the same latest snapshot) can't race into a length that flips the
 * snapshot-vs-live boundary — the "same scrubber index shows different status"
 * bug (#1128).
 */
export function snapshotContentKey(nodes: unknown[], edges: unknown[]): string {
  const nodeKey = (nodes as Array<Record<string, any>>)
    .map((n) => {
      const d = (n?.data ?? {}) as Record<string, unknown>;
      return `${n?.identifier ?? ''}:${d.status ?? ''}:${d.on_active_path === true ? 1 : 0}`;
    })
    .sort()
    .join(',');
  const edgeKey = (edges as Array<Record<string, any>>)
    .map((e) => `${e?.source ?? ''}>${e?.target ?? ''}`)
    .sort()
    .join(',');
  return `${nodeKey}|${edgeKey}`;
}
